
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { CheckCircle, AlertTriangle, XCircle, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';

interface HealthCheckResult {
  status: 'healthy' | 'warning' | 'error';
  message: string;
  details?: any;
}

interface HealthCheckResponse {
  timestamp: string;
  status: 'healthy' | 'warning' | 'error';
  results: {
    auth: HealthCheckResult;
    email: HealthCheckResult;
    database: HealthCheckResult;
    storage: HealthCheckResult;
    functions: HealthCheckResult;
    stripe: HealthCheckResult;
    sentry: HealthCheckResult;
  };
  summary: {
    message: string;
    errorCount: number;
    warningCount: number;
    healthyCount: number;
  };
}

const SystemHealthChecker = () => {
  const [healthData, setHealthData] = useState<HealthCheckResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const toggleSection = (section: string) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter(s => s !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <RefreshCw className="h-5 w-5 text-blue-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'healthy':
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">Gesund</Badge>;
      case 'warning':
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Warnung</Badge>;
      case 'error':
        return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">Fehler</Badge>;
      default:
        return <Badge variant="outline">Unbekannt</Badge>;
    }
  };

  const runHealthCheck = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase.functions.invoke('check-health');
      
      if (error) throw error;
      
      setHealthData(data as HealthCheckResponse);
      setLastChecked(new Date());
      toast({
        title: "Überprüfung abgeschlossen",
        description: data.summary.message,
      });
    } catch (error: any) {
      console.error('Error running health check:', error);
      toast({
        variant: "destructive",
        title: "Fehler bei der Überprüfung",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Run health check on initial load
    runHealthCheck();
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              System Status
              {healthData && getStatusIcon(healthData.status)}
            </CardTitle>
            <CardDescription>
              Systemüberprüfung für kritische Komponenten
              {lastChecked && ` - Zuletzt geprüft: ${lastChecked.toLocaleString()}`}
            </CardDescription>
          </div>
          {healthData && getStatusBadge(healthData.status)}
        </div>
      </CardHeader>
      
      <CardContent>
        {healthData ? (
          <div className="space-y-4">
            {Object.entries(healthData.results).map(([key, result]) => (
              <div key={key} className="border rounded-md overflow-hidden">
                <div
                  className="flex items-center justify-between p-3 cursor-pointer bg-gray-100/5 hover:bg-gray-100/10"
                  onClick={() => toggleSection(key)}
                >
                  <div className="flex items-center gap-2">
                    {getStatusIcon(result.status)}
                    <span className="font-medium capitalize">{key}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500 mr-2">{result.message}</span>
                    {expandedSections.includes(key) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </div>
                </div>
                
                {expandedSections.includes(key) && (
                  <div className="p-3 border-t text-sm">
                    <pre className="whitespace-pre-wrap bg-gray-100/5 p-2 rounded">
                      {JSON.stringify(result.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center p-6">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mb-4" />
            <p>Überprüfe Systemstatus...</p>
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-500">Keine Daten verfügbar</p>
          </div>
        )}
      </CardContent>
      
      <CardFooter>
        <div className="w-full flex justify-between items-center">
          {healthData && (
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">{healthData.summary.healthyCount} Gesund</Badge>
              <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">{healthData.summary.warningCount} Warnungen</Badge>
              <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">{healthData.summary.errorCount} Fehler</Badge>
            </div>
          )}
          <Button 
            onClick={runHealthCheck} 
            disabled={isLoading}
            variant="outline" 
            className="ml-auto"
          >
            {isLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
            Jetzt prüfen
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default SystemHealthChecker;
